import React, { useState, useEffect } from 'react';
import api from '../api';
import {
  BarChart, Bar, LineChart, Line, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';

const GARNET = '#5b1e22';
const COAL = '#181617';
const ASHEN = '#7A766F';
const GREEN = '#1a7a4c';
const BLUE = '#1a5a7a';
const ORANGE = '#b35c00';
const PURPLE = '#6b4c8a';

const VISIT_COLORS = {
  new_piercing: GARNET,
  downsizing: BLUE,
  checkup: ORANGE,
  consultation: PURPLE
};

function fmtHKD(v) { return `$${Math.round(v).toLocaleString()}`; }
function fmtSGD(v) { return `S$${Math.round(v).toLocaleString()}`; }
function fmtHKDAxis(v) {
  if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
  return `$${Math.round(v)}`;
}
function fmtSGDAxis(v) {
  if (v >= 1000000) return `S$${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `S$${(v / 1000).toFixed(0)}K`;
  return `S$${Math.round(v)}`;
}
function makeTicks(data, keys, step) {
  const max = Math.max(0, ...data.map(d => keys.reduce((s, k) => s + (Number(d[k]) || 0), 0)));
  const ceil = Math.ceil(max / step) * step || step;
  const result = [];
  for (let i = 0; i <= ceil; i += step) result.push(i);
  return result;
}
function fmtPeriod(dateStr, period) {
  const d = new Date(dateStr);
  if (period === 'weekly') return `${d.getDate()}/${d.getMonth() + 1}`;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
}
function fmtPeriodShort(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const currentYear = new Date().getFullYear();
  if (d.getFullYear() !== currentYear) return `${months[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`;
  return months[d.getMonth()];
}

function useIsMobile() {
  const [m, setM] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const h = () => setM(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
}

function StatCard({ label, value, sub, color, highlight }) {
  return (
    <div style={{
      background: highlight ? GARNET : '#fff', borderRadius: 8, padding: '16px 20px', minWidth: 0,
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)', borderLeft: `4px solid ${color || GARNET}`
    }}>
      <div style={{ fontSize: 13, color: highlight ? 'rgba(255,255,255,0.7)' : ASHEN, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: highlight ? '#fff' : COAL }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: highlight ? 'rgba(255,255,255,0.6)' : ASHEN, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function PeriodToggle({ period, setPeriod }) {
  return (
    <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: `1px solid ${ASHEN}33`, flexShrink: 0 }}>
      {['weekly', 'monthly'].map(p => (
        <button key={p} onClick={() => setPeriod(p)} style={{
          padding: '6px 12px', fontSize: 12, border: 'none', cursor: 'pointer',
          background: period === p ? GARNET : '#fff', color: period === p ? '#fff' : COAL,
          fontWeight: period === p ? 600 : 400, minHeight: 32, whiteSpace: 'nowrap'
        }}>{p === 'weekly' ? 'Weekly' : 'Monthly'}</button>
      ))}
    </div>
  );
}

function Section({ title, children, style }) {
  return (
    <div style={{ marginBottom: 24, ...style }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: ASHEN, marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}

function Card({ children, style }) {
  return (
    <div style={{ background: '#fff', borderRadius: 8, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', ...style }}>
      {children}
    </div>
  );
}

function CardTitle({ children }) {
  return <div style={{ fontSize: 14, fontWeight: 600, color: COAL, marginBottom: 14 }}>{children}</div>;
}

// ─── Revenue tooltip ────────────────────────────────────────────────
function RevenueTooltip({ active, payload, fmtFn }) {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload || {};
  const total = (p.piercing_revenue || 0) + (p.non_piercing_revenue || 0);
  const fmt = fmtFn || fmtHKD;
  return (
    <div style={{ background: '#fff', border: `1px solid ${ASHEN}33`, borderRadius: 6, padding: '10px 14px', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: COAL, marginBottom: 6 }}>Total: {fmt(total)}</div>
      <div style={{ fontSize: 13, color: GARNET }}>Piercing: {fmt(p.piercing_revenue || 0)}</div>
      <div style={{ fontSize: 13, color: ASHEN }}>Non-Piercing: {fmt(p.non_piercing_revenue || 0)}</div>
      <div style={{ fontSize: 11, color: ASHEN, marginTop: 4, paddingTop: 4, borderTop: `1px solid ${ASHEN}22` }}>{p.order_count} orders</div>
    </div>
  );
}

// ─── Visit Volume tooltip (shows segment + total for stacked bars) ──
function VisitTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div style={{ background: '#fff', border: `1px solid ${ASHEN}33`, borderRadius: 6, padding: '10px 14px', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: COAL, marginBottom: 4 }}>{label} — Total: {total}</div>
      {payload.map(p => p.value > 0 && (
        <div key={p.dataKey} style={{ fontSize: 12, color: p.fill, marginTop: 2 }}>{p.name}: {p.value}</div>
      ))}
    </div>
  );
}

// ─── Peak Heatmap ────────────────────────────────────────────────────
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOURS = Array.from({ length: 14 }, (_, i) => i + 9); // 9am–10pm

function PeakHeatmap({ matrix, maxCount, isMobile }) {
  if (!matrix) return <div style={{ color: ASHEN, fontSize: 13 }}>No data yet</div>;

  function cellColor(count) {
    if (!count) return '#f7f7f7';
    const intensity = Math.min(count / maxCount, 1);
    const r = Math.round(247 - (247 - 91) * intensity);
    const g = Math.round(247 - (247 - 30) * intensity);
    const b = Math.round(247 - (247 - 34) * intensity);
    return `rgb(${r},${g},${b})`;
  }

  const cellSize = isMobile ? 28 : 38;
  const labelW = isMobile ? 30 : 40;

  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ display: 'inline-block', minWidth: (DAYS.length + 1) * cellSize + labelW }}>
        {/* Day headers */}
        <div style={{ display: 'flex', marginLeft: labelW }}>
          {DAYS.map(d => (
            <div key={d} style={{ width: cellSize, textAlign: 'center', fontSize: 11, color: ASHEN, fontWeight: 600, paddingBottom: 4 }}>{d}</div>
          ))}
        </div>
        {/* Hour rows */}
        {HOURS.map(h => (
          <div key={h} style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
            <div style={{ width: labelW, fontSize: 10, color: ASHEN, textAlign: 'right', paddingRight: 6 }}>
              {h % 12 === 0 ? 12 : h % 12}{h < 12 ? 'am' : 'pm'}
            </div>
            {DAYS.map((_, dow) => {
              const count = matrix[`${dow}_${h}`] || 0;
              return (
                <div key={dow} title={count ? `${DAYS[dow]} ${h}:00 — ${count} visits` : ''}
                  style={{
                    width: cellSize - 2, height: cellSize - 2, margin: 1,
                    background: cellColor(count), borderRadius: 4,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, color: count > maxCount * 0.5 ? '#fff' : ASHEN,
                    fontWeight: count > 0 ? 600 : 400
                  }}>
                  {count > 0 ? count : ''}
                </div>
              );
            })}
          </div>
        ))}
        {/* Legend */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, marginLeft: labelW, fontSize: 11, color: ASHEN }}>
          <span>Low</span>
          {[0.1, 0.3, 0.5, 0.7, 1.0].map(v => (
            <div key={v} style={{ width: 16, height: 16, borderRadius: 3, background: cellColor(Math.round(v * maxCount)) }} />
          ))}
          <span>High</span>
        </div>
      </div>
    </div>
  );
}

// ─── Placement horizontal bars ────────────────────────────────────────
function PlacementBars({ placements, isMobile }) {
  if (!placements?.length) return <div style={{ color: ASHEN, fontSize: 13 }}>No data yet</div>;
  const max = placements[0]?.pct || 1;
  const trendIcon = { up: '↑', down: '↓', flat: '→' };
  const trendColor = { up: GREEN, down: GARNET, flat: ASHEN };

  return (
    <div>
      {placements.slice(0, 10).map((p, i) => (
        <div key={p.placement} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
            <span style={{ fontSize: 13, color: COAL, fontWeight: 500 }}>{p.label}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, color: trendColor[p.trend], fontWeight: 700 }}>{trendIcon[p.trend]}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: COAL }}>{p.pct}%</span>
              <span style={{ fontSize: 11, color: ASHEN }}>({p.count})</span>
            </div>
          </div>
          <div style={{ height: 6, background: '#f0ede9', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(p.pct / max) * 100}%`, background: COLORS_10[i % COLORS_10.length], borderRadius: 3, transition: 'width 0.4s' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

const COLORS_10 = [GARNET, '#8B4513', BLUE, ASHEN, PURPLE, ORANGE, '#1a7a4c', '#b32d2d', '#4a6fa5', '#5c4a1e'];

export default function Dashboard() {
  const isMobile = useIsMobile();
  const [revPeriod, setRevPeriod] = useState('monthly');
  const [country, setCountry] = useState('all'); // 'all' | 'HK' | 'SG'
  const [studioId, setStudioId] = useState(null); // store sub-tab within a country (null = all stores)
  const [studios, setStudios] = useState([]); // active studios, for the store sub-tabs
  const [fxRate, setFxRate] = useState(null); // SGD→HKD rate

  // Data state
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [revBreakdown, setRevBreakdown] = useState(null);
  const [visitBreakdown, setVisitBreakdown] = useState(null);
  const [repeatRate, setRepeatRate] = useState(null);
  const [serviceConv, setServiceConv] = useState(null);
  const [newCustomers, setNewCustomers] = useState(null); // kept for potential future use
  const [downsizingHealth, setDowHealth] = useState(null);
  const [dailyRunRate, setDailyRunRate] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [referralSources, setReferralSources] = useState(null); // unused — removed from UI
  const [placements, setPlacements] = useState(null);
  const [loading, setLoading] = useState(true);

  // Company → Country → Store: country always passed ('all' = company total); studio_id narrows to one store
  const cp = `country=${country}${studioId ? `&studio_id=${studioId}` : ''}`;

  useEffect(() => { loadStatic(); }, [country, studioId]); // eslint-disable-line
  useEffect(() => { loadRevenue(); }, [revPeriod, country, studioId]); // eslint-disable-line

  // Fetch FX rate once (for the "All" tab note) and the studio list for the store sub-tabs
  useEffect(() => {
    api.get('/dashboard/fx-rate').then(r => setFxRate(r.data.sgd_to_hkd)).catch(() => {});
    api.get('/booking/studios').then(r => setStudios(r.data.studios || [])).catch(() => {});
  }, []);

  async function loadStatic() {
    setLoading(true);
    try {
      const [statsRes, heatRes, healthRes, visitRes, repeatRes, convRes, placeRes, runRateRes] = await Promise.all([
        api.get(`/dashboard/stats?${cp}`),
        api.get(`/dashboard/peak-heatmap?${cp}`),
        api.get(`/dashboard/downsizing-health?${cp}`),
        api.get(`/dashboard/visit-breakdown?period=monthly&${cp}`),
        api.get(`/dashboard/repeat-rate?${cp}`),
        api.get(`/dashboard/service-conversion?${cp}`),
        api.get(`/dashboard/placement-breakdown?period=monthly&${cp}`),
        api.get(`/dashboard/daily-run-rate?${cp}`)
      ]);
      setStats(statsRes.data);
      setHeatmap(heatRes.data);
      setDowHealth(healthRes.data);
      setVisitBreakdown(visitRes.data);
      setRepeatRate(repeatRes.data);
      setServiceConv(convRes.data);
      setPlacements(placeRes.data);
      setDailyRunRate(runRateRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function loadRevenue() {
    try {
      const [revRes, breakRes] = await Promise.all([
        api.get(`/dashboard/revenue?period=${revPeriod}&${cp}`),
        api.get(`/dashboard/revenue-breakdown?period=${revPeriod}&${cp}`)
      ]);
      setRevenue(revRes.data);
      setRevBreakdown(breakRes.data);
    } catch (e) { console.error(e); }
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: ASHEN }}>Loading dashboard...</div>;

  // Currency helpers based on selected country tab
  const fmtRev = country === 'SG' ? fmtSGD : fmtHKD;
  const fmtRevAxis = country === 'SG' ? fmtSGDAxis : fmtHKDAxis;
  const currencyLabel = country === 'SG' ? 'SGD' : 'HKD';

  // Store sub-tabs: only meaningful inside a country with more than one studio
  const countryStudios = studios.filter(s => s.country === country);
  const selectedStudio = countryStudios.find(s => s.id === studioId) || null;
  const scopeLabel = selectedStudio ? selectedStudio.name : (country === 'all' ? 'All countries' : `All ${country}`);

  // Derived data
  const revTrend = revenue?.trend?.map(r => ({ ...r, label: fmtPeriod(r.period, revPeriod) })) || [];
  const visitTrend = visitBreakdown?.trend?.map(r => ({ ...r, label: fmtPeriodShort(r.period) })) || [];
  const repeatTrend = repeatRate?.trend?.map(r => ({ ...r, label: fmtPeriodShort(r.period) })) || [];
  // new buyers derived from repeat rate data so both charts use the same source
  const newCustTrend = repeatTrend;
  const convTrend = serviceConv?.trend?.map(r => ({ ...r, label: fmtPeriodShort(r.period) })) || [];
  const breakdownData = revBreakdown?.breakdown || [];
  const breakdownTotal = breakdownData.reduce((s, b) => s + b.revenue, 0);

  // Latest / prior repeat rate (last entry = current partial month, second-to-last = prior full month)
  const latestRepeat = repeatTrend.length ? repeatTrend[repeatTrend.length - 1] : null;
  const priorRepeat = repeatTrend.length > 1 ? repeatTrend[repeatTrend.length - 2] : null;
  const latestConv = convTrend.length ? convTrend[convTrend.length - 1] : null;
  const health = downsizingHealth?.summary;

  // Revenue projection: (revenue through yesterday / completed days) × days in month
  // Uses yesterday's closing number so today's partial trading doesn't drag the daily average down
  const today = new Date();
  const completedDays = today.getDate() - 1;
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const projectedRevenue = revenue && completedDays > 0
    ? Math.round((revenue.this_month.revenue_through_yesterday / completedDays) * daysInMonth)
    : null;

  const grid2 = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 };

  // Shared KPI card style
  const kpiCard = (borderColor) => ({
    background: '#fff', borderRadius: 8, padding: '14px 18px', minWidth: 0,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)', borderLeft: `4px solid ${borderColor || GARNET}`
  });

  return (
    <div>
      {/* Header + country tabs */}
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ margin: 0, color: COAL, fontSize: isMobile ? 20 : 22 }}>Dashboard</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {country === 'all' && fxRate && (
            <span style={{ fontSize: 11, color: ASHEN }}>~1 SGD = {fxRate.toFixed(2)} HKD</span>
          )}
          <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: 6, padding: 2 }}>
            {[{ key: 'all', label: 'All' }, { key: 'HK', label: 'HK' }, { key: 'SG', label: 'SG' }].map(f => (
              <button key={f.key} onClick={() => { setCountry(f.key); setStudioId(null); }} style={{
                padding: '5px 14px', border: 'none', borderRadius: 5, fontSize: 13, fontWeight: 600,
                cursor: 'pointer', background: country === f.key ? '#fff' : 'transparent',
                color: country === f.key ? COAL : ASHEN,
                boxShadow: country === f.key ? '0 1px 2px rgba(0,0,0,0.08)' : 'none'
              }}>{f.label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* ─── Store sub-tabs (Country → Store) ───────────────────── */}
      {country !== 'all' && countryStudios.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: ASHEN, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Store</span>
          <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: 6, padding: 2, flexWrap: 'wrap' }}>
            {[{ id: null, name: `All ${country}` }, ...countryStudios].map(s => {
              const active = studioId === s.id;
              return (
                <button key={s.id || 'all'} onClick={() => setStudioId(s.id)} style={{
                  padding: '5px 14px', border: 'none', borderRadius: 5, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', background: active ? '#fff' : 'transparent',
                  color: active ? COAL : ASHEN, boxShadow: active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none'
                }}>{s.name}</button>
              );
            })}
          </div>
          {selectedStudio && (
            <span style={{ fontSize: 11, color: ASHEN }}>Store view · LTV &amp; VIP stay at country level</span>
          )}
        </div>
      )}

      {/* ─── KPI Cards ─────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 12, marginBottom: 24 }}>
        {/* Revenue MTD + projected */}
        <div style={kpiCard(GARNET)}>
          <div style={{ fontSize: 13, color: ASHEN, marginBottom: 4 }}>Revenue (MTD)</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: COAL }}>{revenue ? fmtRev(revenue.this_month.revenue) : '...'}</div>
          {projectedRevenue !== null && (
            <div style={{ fontSize: 12, color: ASHEN, marginTop: 3 }}>
              Proj full mo: <span style={{ color: COAL, fontWeight: 600 }}>{fmtRev(projectedRevenue)}</span>
            </div>
          )}
        </div>
        {/* Online (non-service) revenue MTD */}
        <div style={kpiCard(BLUE)}>
          <div style={{ fontSize: 13, color: ASHEN, marginBottom: 4 }}>Online Revenue (MTD)</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: COAL }}>
            {revenue ? `${fmtRev(revenue.online_mtd?.revenue || 0)} (${revenue.online_mtd?.orders || 0})` : '...'}
          </div>
          {revenue?.online_prior && (
            <div style={{ fontSize: 12, color: ASHEN, marginTop: 3 }}>
              Prior mo: <span style={{ color: COAL, fontWeight: 600 }}>{fmtRev(revenue.online_prior.revenue)} ({revenue.online_prior.orders})</span>
            </div>
          )}
        </div>
        {/* Piercings MTD + prior month */}
        <div style={kpiCard(ORANGE)}>
          <div style={{ fontSize: 13, color: ASHEN, marginBottom: 4 }}>Piercings (MTD)</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: COAL }}>{stats?.piercings_this_month?.toLocaleString() ?? '...'}</div>
          {stats?.piercings_prior_month !== undefined && (
            <div style={{ fontSize: 12, color: ASHEN, marginTop: 3 }}>
              Prior mo: <span style={{ color: COAL, fontWeight: 600 }}>{stats.piercings_prior_month}</span>
            </div>
          )}
        </div>
        {/* Repeat Rate MTD + prior full month */}
        <div style={kpiCard(GREEN)}>
          <div style={{ fontSize: 13, color: ASHEN, marginBottom: 4 }}>Repeat Rate (MTD)</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: COAL }}>{latestRepeat ? `${latestRepeat.repeat_pct}%` : '...'}</div>
          {priorRepeat && (
            <div style={{ fontSize: 12, color: ASHEN, marginTop: 3 }}>
              Prior mo: <span style={{ color: COAL, fontWeight: 600 }}>{priorRepeat.repeat_pct}%</span>
            </div>
          )}
        </div>
      </div>

      {/* ─── Revenue ───────────────────────────────────────────── */}
      <Section title="Revenue">
        <div style={grid2}>
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: COAL }}>Revenue Trend ({currencyLabel}) — {revPeriod === 'weekly' ? 'Last 12 Weeks' : 'Last 12 Months'}</div>
              <PeriodToggle period={revPeriod} setPeriod={setRevPeriod} />
            </div>
            <ResponsiveContainer width="100%" height={isMobile ? 200 : 260}>
              <BarChart data={revTrend} margin={{ left: isMobile ? 4 : 0, right: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="label" tick={{ fontSize: isMobile ? 10 : 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtRevAxis} width={isMobile ? 50 : 60}
                  ticks={makeTicks(revTrend, ['piercing_revenue','non_piercing_revenue'], revPeriod === 'weekly' ? 75000 : 250000)} />
                <Tooltip content={<RevenueTooltip fmtFn={fmtRev} />} />
                <Bar dataKey="piercing_revenue" name="Piercing" fill={GARNET} stackId="a" />
                <Bar dataKey="non_piercing_revenue" name="Non-Piercing" fill={ASHEN} stackId="a" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 8 }}>
              {[{ c: GARNET, l: 'Piercing' }, { c: ASHEN, l: 'Non-Piercing' }].map(x => (
                <div key={x.l} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 2, background: x.c }} /> {x.l}
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <CardTitle>Revenue by Category</CardTitle>
            <div style={{ marginBottom: 12 }}>
              {breakdownData.map((b, i) => {
                const pct = breakdownTotal > 0 ? Math.round(b.revenue / breakdownTotal * 100) : 0;
                return (
                  <div key={i} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                      <span style={{ fontSize: 13, color: COAL }}>{b.category}</span>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <span style={{ fontSize: 13, color: ASHEN }}>{pct}%</span>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{fmtRev(b.revenue)}</span>
                      </div>
                    </div>
                    <div style={{ height: 6, background: '#f0ede9', borderRadius: 3 }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: COLORS_10[i], borderRadius: 3 }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8, marginTop: 16, borderTop: `1px solid #f0ede9`, paddingTop: 16 }}>
              {revenue && ['1m','3m','6m','1y'].map(k => (
                <div key={k} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: ASHEN }}>{k === '1m' ? '1 Mo AOV' : k === '3m' ? '3 Mo AOV' : k === '6m' ? '6 Mo AOV' : '1 Yr AOV'}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: COAL }}>{fmtRev(revenue.aov[k]?.aov || 0)}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </Section>

      {/* ─── Studio Activity ───────────────────────────────────── */}
      <Section title="Studio Activity">
        <div style={grid2}>
          <Card>
            <CardTitle>Visit Volume by Type</CardTitle>
            <ResponsiveContainer width="100%" height={isMobile ? 200 : 220}>
              <BarChart data={visitTrend} margin={{ left: isMobile ? 4 : 0, right: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={36}
                  ticks={makeTicks(visitTrend, ['new_piercing','downsizing','checkup','consultation'], 100)} />
                <Tooltip content={<VisitTooltip />} />
                <Bar dataKey="new_piercing" name="New Piercing" fill={GARNET} stackId="a" />
                <Bar dataKey="downsizing" name="Downsizing" fill={BLUE} stackId="a" />
                <Bar dataKey="checkup" name="Check-up" fill={ORANGE} stackId="a" />
                <Bar dataKey="consultation" name="Consultation" fill={PURPLE} stackId="a" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', marginTop: 8 }}>
              {[['new_piercing','New Piercing',GARNET],['downsizing','Downsizing',BLUE],['checkup','Check-up',ORANGE],['consultation','Consultation',PURPLE]].map(([k,l,c]) => (
                <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 2, background: c }} /> {l}
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <CardTitle>New Buyers per Month</CardTitle>
            <ResponsiveContainer width="100%" height={isMobile ? 200 : 220}>
              <BarChart data={newCustTrend} margin={{ left: isMobile ? 4 : 0, right: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={36}
                  ticks={makeTicks(newCustTrend, ['new_buyers'], 100)} />
                <Tooltip formatter={(v) => [v, 'New Buyers']} />
                <Bar dataKey="new_buyers" name="New Buyers" fill={GREEN} radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>
      </Section>

      {/* ─── Customer Loyalty ─────────────────────────────────── */}
      <Section title="Customer Loyalty">
        <Card>
          <CardTitle>Repeat Customer Rate — Monthly</CardTitle>
          <ResponsiveContainer width="100%" height={isMobile ? 180 : 220}>
            <ComposedChart data={repeatTrend} margin={isMobile ? { left: -20, right: 5 } : undefined}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="count" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="pct" orientation="right" domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} />
              <Tooltip formatter={(v, name) => name === 'Repeat %' ? `${v}%` : v} />
              <Bar yAxisId="count" dataKey="total_buyers" name="Total Buyers" fill="#c0b9b2" radius={[3,3,0,0]} />
              <Bar yAxisId="count" dataKey="repeat_buyers" name="Repeat Buyers" fill={GARNET} opacity={0.7} radius={[3,3,0,0]} />
              <Line yAxisId="pct" type="monotone" dataKey="repeat_pct" name="Repeat %" stroke={GREEN} strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'center', marginTop: 8 }}>
            {[['#c0b9b2','Total Buyers'],[GARNET,'Repeat Buyers'],[GREEN,'Repeat %']].map(([c,l]) => (
              <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: c }} /> {l}
              </div>
            ))}
          </div>
        </Card>
      </Section>

      {/* ─── Service Intelligence ─────────────────────────────── */}
      <Section title="Service Intelligence">
        <div style={grid2}>
          <Card>
            <CardTitle>Free Service → Purchase Conversion</CardTitle>
            <ResponsiveContainer width="100%" height={isMobile ? 180 : 200}>
              <ComposedChart data={convTrend} margin={isMobile ? { left: -20, right: 5 } : undefined}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="pct" domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} />
                <YAxis yAxisId="count" orientation="right" tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v, name) => name === 'Conversion %' ? `${v}%` : v} />
                <Bar yAxisId="count" dataKey="total_free_visits" name="Free Visits" fill="#c0b9b2" radius={[3,3,0,0]} />
                <Bar yAxisId="count" dataKey="converted" name="Converted" fill={GREEN} opacity={0.8} radius={[3,3,0,0]} />
                <Line yAxisId="pct" type="monotone" dataKey="conversion_pct" name="Conversion %" stroke={GARNET} strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
            {latestConv && (
              <div style={{ display: 'flex', gap: 16, marginTop: 14, paddingTop: 14, borderTop: `1px solid #f0ede9` }}>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: latestConv.conversion_pct >= 20 ? GREEN : GARNET }}>{latestConv.conversion_pct}%</div>
                  <div style={{ fontSize: 11, color: ASHEN }}>This month</div>
                </div>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: COAL }}>{latestConv.total_free_visits}</div>
                  <div style={{ fontSize: 11, color: ASHEN }}>Free visits</div>
                </div>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: COAL }}>{latestConv.converted}</div>
                  <div style={{ fontSize: 11, color: ASHEN }}>Converted</div>
                </div>
              </div>
            )}
          </Card>

          <Card>
            <CardTitle>Downsizing Readiness</CardTitle>
            {health && health.total > 0 ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 16 }}>
                  {[
                    { label: 'Ready', val: health.ready, pct: Math.round(health.ready / health.total * 100), color: GREEN },
                    { label: 'Not Ready', val: health.not_ready, pct: Math.round(health.not_ready / health.total * 100), color: GARNET },
                    { label: 'Not Needed', val: health.not_needed, pct: Math.round(health.not_needed / health.total * 100), color: ASHEN }
                  ].map(item => (
                    <div key={item.label} style={{ textAlign: 'center', padding: '12px 4px', background: '#fafafa', borderRadius: 8 }}>
                      <div style={{ fontSize: 22, fontWeight: 700, color: item.color }}>{item.pct}%</div>
                      <div style={{ fontSize: 12, color: COAL, fontWeight: 500 }}>{item.label}</div>
                      <div style={{ fontSize: 11, color: ASHEN }}>{item.val} visits</div>
                    </div>
                  ))}
                </div>
                <div style={{ height: 8, background: '#f0ede9', borderRadius: 4, overflow: 'hidden', display: 'flex' }}>
                  <div style={{ width: `${Math.round(health.ready / health.total * 100)}%`, background: GREEN }} />
                  <div style={{ width: `${Math.round(health.not_ready / health.total * 100)}%`, background: GARNET }} />
                  <div style={{ width: `${Math.round(health.not_needed / health.total * 100)}%`, background: ASHEN }} />
                </div>
                <div style={{ fontSize: 11, color: ASHEN, marginTop: 8, textAlign: 'center' }}>{health.total} total downsizing checks</div>
              </>
            ) : (
              <div style={{ color: ASHEN, fontSize: 13 }}>No downsizing data yet</div>
            )}
            {downsizingHealth && (
              <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #f0ede9' }}>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: ASHEN, marginBottom: 8 }}>Visits vs Outcomes — Last 3 Months</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1, textAlign: 'center', padding: '10px 4px', background: '#fafafa', borderRadius: 8 }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: COAL }}>{downsizingHealth.visits_3m}</div>
                    <div style={{ fontSize: 11, color: ASHEN }}>Downsizing visits</div>
                  </div>
                  <div style={{ flex: 1, textAlign: 'center', padding: '10px 4px', background: '#fafafa', borderRadius: 8 }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: COAL }}>{downsizingHealth.outcomes_3m}</div>
                    <div style={{ fontSize: 11, color: ASHEN }}>Outcomes recorded</div>
                  </div>
                  <div style={{ flex: 1, textAlign: 'center', padding: '10px 4px', background: '#fafafa', borderRadius: 8 }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: downsizingHealth.completion_rate_3m >= 80 ? GREEN : downsizingHealth.completion_rate_3m >= 50 ? ORANGE : GARNET }}>
                      {downsizingHealth.completion_rate_3m}%
                    </div>
                    <div style={{ fontSize: 11, color: ASHEN }}>Completion rate</div>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
      </Section>

      {/* ─── Piercing Insights ────────────────────────────────── */}
      <Section title="Piercing Insights">
        <div style={grid2}>
          <Card>
            <CardTitle>Placement Breakdown — % Share (12 mo)</CardTitle>
            <PlacementBars placements={placements?.placements} isMobile={isMobile} />
          </Card>

          <Card>
            <CardTitle>{selectedStudio ? `LTV & VIP Customers — ${country} country-wide` : 'LTV & VIP Customers'}</CardTitle>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: 8 }}>
              {revenue && [
                { label: 'Avg LTV', val: fmtRev(revenue.ltv.average), sub: `${revenue.ltv.total_customers} customers` },
                { label: `VIP (Single >$${(revenue.ltv.vip_threshold || 8000) / 1000}K)`, val: revenue.ltv.vip_single_checkout_count, sub: 'customers' },
                { label: `VIP (Lifetime >$${(revenue.ltv.vip_threshold || 8000) / 1000}K)`, val: revenue.ltv.vip_lifetime_count, sub: 'customers' }
              ].map(item => (
                <div key={item.label} style={{ textAlign: 'center', padding: '14px 8px', background: '#fafafa', borderRadius: 8 }}>
                  <div style={{ fontSize: 11, color: ASHEN, marginBottom: 4 }}>{item.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: COAL }}>{item.val}</div>
                  <div style={{ fontSize: 11, color: ASHEN }}>{item.sub}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </Section>

      {/* ─── Operations ───────────────────────────────────────── */}
      <Section title="Operations">
        <Card>
          <CardTitle>Check-in Volume by Day & Hour</CardTitle>
          <PeakHeatmap matrix={heatmap?.matrix} maxCount={heatmap?.max_count || 1} isMobile={isMobile} />
        </Card>
        {dailyRunRate && (
          <Card style={{ marginTop: 16 }}>
            <CardTitle>Avg Net Revenue by Day of Week (HKD)</CardTitle>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? 12 : 14 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px 10px', color: ASHEN, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `2px solid #f0ede9`, width: 70 }}>Window</th>
                    {dailyRunRate.days.map(d => (
                      <th key={d} style={{ textAlign: 'right', padding: '8px 10px', color: ASHEN, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `2px solid #f0ede9` }}>{d}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[['1mo', '1 Month'], ['3mo', '3 Months'], ['6mo', '6 Months']].map(([key, label]) => {
                    const vals = dailyRunRate.windows[key];
                    const max = Math.max(...vals);
                    const min = Math.min(...vals.filter(v => v > 0));
                    return (
                      <tr key={key} style={{ borderBottom: '1px solid #f0ede9' }}>
                        <td style={{ padding: '10px 10px', color: ASHEN, fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>{label}</td>
                        {vals.map((v, i) => {
                          const isMax = v === max && max > 0;
                          const isMin = v === min && min < max;
                          const bg = isMax ? '#e8f5ee' : isMin ? '#fdf0ee' : 'transparent';
                          const color = isMax ? GREEN : isMin ? GARNET : COAL;
                          return (
                            <td key={i} style={{ textAlign: 'right', padding: '10px 10px', fontWeight: isMax || isMin ? 700 : 400, color, background: bg, borderRadius: 4 }}>
                              {v > 0 ? fmtRev(v) : '—'}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 11, color: ASHEN, marginTop: 10 }}>Net revenue (after refunds) ÷ number of occurrences of that day in the window. {currencyLabel} only.</div>
          </Card>
        )}
      </Section>
    </div>
  );
}
